import { Action } from 'redux';
import {
  ConciliationStatus,
  DashboardAutoUpdate,
  HttpErrorBaseAction,
  MastersStatus,
  RemoteResource,
  ConciliationsStateTimeLapse,
  SovosImportStatus,
  DashboardTotalsByCountry,
  ClosureEvolutionRow,
  HttpRequestError,
} from '../../types';

export interface ConciliationStatuses {
  multiCountry: RemoteResource<ConciliationStatus[]>;
  yesterday: RemoteResource<ConciliationStatus[]>;
  currentMonth: RemoteResource<ConciliationStatus[]>;
  lastMonth: RemoteResource<ConciliationStatus[]>;
}

export interface ConciliationTotals {
  yesterday: RemoteResource<DashboardTotalsByCountry[]>;
  currentMonth: RemoteResource<DashboardTotalsByCountry[]>;
  lastMonth: RemoteResource<DashboardTotalsByCountry[]>;
}
export interface State {
  conciliations: {
    singleCountry: boolean;
    activeCountry: string | null;
    statuses: ConciliationStatuses;
    totals: ConciliationTotals;
  };
  autoUpdate: DashboardAutoUpdate;
  fiscalStatus: {
    sovosImportStatuses: RemoteResource<SovosImportStatus[]>;
  };
  masterStatus2: RemoteResource<MastersStatus[]>;
  closureEvolution: RemoteResource<ClosureEvolutionRow[]>;
  closureEvolutionFilters: {
    startDate: Date | null;
    endDate: Date | null;
  };
}

export enum ActionType {
  changeAutoUpdate = 'dashboard/changeAutoUpdate',
  findAllRequest = 'dashboard/findAllRequest',
  findSovosImportStatusesRequested = 'dashboard/findSovosStatusRequested',
  findSovosImportStatusesSuccess = 'dashboard/findSovosStatusSuccess',
  findSovosImportStatusesFailure = 'dashboard/findSovosStatusFailure',
  findMastersStatus2Requested = 'dashboard/findMastersStatus2Requested',
  findMastersStatus2Success = 'dashboard/findMastersStatus2Success',
  findMastersStatus2Failure = 'dashboard/findMastersStatus2Failure',
  findDashboardConciliationsStateRequested = 'dashboard/findDashboardConciliationsStateRequested',
  findDashboardConciliationsStateSuccess = 'dashboard/findDashboardConciliationsStateSuccess',
  findDashboardConciliationsStateFailure = 'dashboard/findDashboardConciliationsStateFailure',
  changeDashboardCountrySelected = 'dashboard/changeDashboardCountrySelected',
  setSingleCountryDashboard = 'dashboard/setSingleCountryDashboard',
  findDashboardConciliationsStateCardsRequested = 'dashboard/findDashboardConciliationsStateCardsRequested',
  getDashboardConciliationsStateYesterdaySuccess = 'dashboard/findDashboardConciliationsStateYesterdaySuccess',
  getDashboardConciliationsStateYesterdayFailure = 'dashboard/findDashboardConciliationsStateYesterdayFailure',
  getDashboardConciliationsStateCurrentMonthSuccess = 'dashboard/findDashboardConciliationsStateCurrentMonthSuccess',
  getDashboardConciliationsStateCurrentMonthFailure = 'dashboard/findDashboardConciliationsStateCurrentMonthFailure',
  getDashboardConciliationsStateLastMonthSuccess = 'dashboard/findDashboardConciliationsStateLastMonthSuccess',
  getDashboardConciliationsStateLastMonthFailure = 'dashboard/findDashboardConciliationsStateLastMonthFailure',
  findDashboardTotalsCardsRequested = 'dashboard/findDashboardTotalsCardsRequested',
  getDashboardTotalsYesterdaySuccess = 'dashboard/getDashboardTotalsYesterdaySuccess',
  getDashboardTotalsYesterdayFailure = 'dashboard/getDashboardTotalsYesterdayFailure',
  getDashboardTotalsCurrentMonthSuccess = 'dashboard/getDashboardTotalsCurrentMonthSuccess',
  getDashboardTotalsCurrentMonthFailure = 'dashboard/getDashboardTotalsCurrentMonthFailure',
  getDashboardTotalsLastMonthSuccess = 'dashboard/getDashboardTotalsLastMonthSuccess',
  getDashboardTotalsLastMonthFailure = 'dashboard/getDashboardTotalsLastMonthFailure',
  findDashboardClosureEvolutionRequested = 'dashboard/findDashboardClosureEvolutionRequested',
  getDashboardClosureEvolutionSuccess = 'dashboard/getDashboardClosureEvolutionSuccess',
  getDashboardClosureEvolutionFailure = 'dashboard/getDashboardClosureEvolutionFailure',
  changeDashboardClosureEvolutionFilterStartAccountingDate = 'dashboard/changeDashboardClosureEvolutionFilterStartAccountingDate',
  changeDashboardClosureEvolutionFilterEndAccountingDate = 'dashboard/changeDashboardClosureEvolutionFilterEndAccountingDate',
}

export type FindDashboardConciliationsStateRequestedAction =
  Action<ActionType.findDashboardConciliationsStateRequested>;

export type FindDashboardConciliationsStateSuccessAction =
  Action<ActionType.findDashboardConciliationsStateSuccess> & {
    conciliationsState: ConciliationStatus[];
  };

export type FindDashboardConciliationsStateFailureAction =
  HttpErrorBaseAction<ActionType.findDashboardConciliationsStateFailure>;

export type ChangeDashboardCountrySelectedAction =
  Action<ActionType.changeDashboardCountrySelected> & {
    countryCode: string;
  };

export type SetSingleCountryDashboardAction = Action<ActionType.setSingleCountryDashboard> & {
  countryCode: string;
};

// ================================================================================
// Conciliations State Cards
// ================================================================================

export type FindDashboardConciliationsStateCardsRequestedAction =
  Action<ActionType.findDashboardConciliationsStateCardsRequested> & {
    countryCode: string;
  };

export type GetDashboardConciliationsStateYesterdaySuccessAction =
  Action<ActionType.getDashboardConciliationsStateYesterdaySuccess> & {
    conciliationsStatusYesterday: ConciliationStatus[];
  };

export type GetDashboardConciliationsStateYesterdayFailureAction =
  HttpErrorBaseAction<ActionType.getDashboardConciliationsStateYesterdayFailure>;

export type GetDashboardConciliationsStateCurrentMonthSuccessAction =
  Action<ActionType.getDashboardConciliationsStateCurrentMonthSuccess> & {
    conciliationsStatusCurrentMonth: ConciliationStatus[];
  };

export type GetDashboardConciliationsStateCurrentMonthFailureAction =
  HttpErrorBaseAction<ActionType.getDashboardConciliationsStateCurrentMonthFailure>;

export type GetDashboardConciliationsStateLastMonthSuccessAction =
  Action<ActionType.getDashboardConciliationsStateLastMonthSuccess> & {
    conciliationsStatusLastMonth: ConciliationStatus[];
  };

export type GetDashboardConciliationsStateLastMonthFailureAction =
  HttpErrorBaseAction<ActionType.getDashboardConciliationsStateLastMonthFailure>;

// ================================================================================

export type ChangeAutoUpdateAction = Action<ActionType.changeAutoUpdate> & {
  autoUpdate: DashboardAutoUpdate;
};

export type FindAllRequestAction = Action<ActionType.findAllRequest>;

export type FindSovosImportStatusesRequestedAction =
  Action<ActionType.findSovosImportStatusesRequested>;

export type FindSovosImportStatusesSuccessAction =
  Action<ActionType.findSovosImportStatusesSuccess> & {
    sovosImportStatuses: SovosImportStatus[];
  };

export type FindSovosImportStatusesFailureAction =
  HttpErrorBaseAction<ActionType.findSovosImportStatusesFailure>;

export type FindMastersStatus2RequestedAction = Action<ActionType.findMastersStatus2Requested>;

export type FindMastersStatus2SuccessAction = Action<ActionType.findMastersStatus2Success> & {
  mastersStatus: MastersStatus[];
};

export type FindMastersStatus2FailureAction =
  HttpErrorBaseAction<ActionType.findMastersStatus2Failure>;
// ---------------------------------------------
// CONCILIATION TOTALS CARDS
//--------------------------------------------

export type FindDashboardTotalsCardsRequestedAction =
  Action<ActionType.findDashboardTotalsCardsRequested> & { countryCode: string };

export type GetDashboardTotalsYesterdaySuccessAction =
  Action<ActionType.getDashboardTotalsYesterdaySuccess> & {
    totalsYesterday: DashboardTotalsByCountry[];
  };

export type GetDashboardTotalsYesterdayFailureAction =
  HttpErrorBaseAction<ActionType.getDashboardTotalsYesterdayFailure>;

export type GetDashboardTotalsCurrentMonthSuccessAction =
  Action<ActionType.getDashboardTotalsCurrentMonthSuccess> & {
    totalsCurrentMonth: DashboardTotalsByCountry[];
  };

export type GetDashboardTotalsCurrentMonthFailureAction =
  HttpErrorBaseAction<ActionType.getDashboardTotalsCurrentMonthFailure>;

export type GetDashboardTotalsLastMonthSuccessAction =
  Action<ActionType.getDashboardTotalsLastMonthSuccess> & {
    totalsLastMonth: DashboardTotalsByCountry[];
  };

export type GetDashboardTotalsLastMonthFailureAction =
  HttpErrorBaseAction<ActionType.getDashboardTotalsLastMonthFailure>;

export type FindDashboardClosureEvolutionRequestedAction =
  Action<ActionType.findDashboardClosureEvolutionRequested> & {
    countryCode: string;
  };

export type GetDashboardClosureEvolutionSuccessAction =
  Action<ActionType.getDashboardClosureEvolutionSuccess> & {
    closureEvolution: ClosureEvolutionRow[];
  };

export type GetDashboardClosureEvolutionFailureAction =
  HttpErrorBaseAction<ActionType.getDashboardClosureEvolutionFailure>;

export type ChangeDashboardClosureEvolutionFilterStartAccountingDateAction =
  Action<ActionType.changeDashboardClosureEvolutionFilterStartAccountingDate> & {
    startDate: Date;
  };

export type ChangeDashboardClosureEvolutionFilterEndAccountingDateAction =
  Action<ActionType.changeDashboardClosureEvolutionFilterEndAccountingDate> & {
    endDate: Date;
  };

export type Actions =
  | FindDashboardConciliationsStateRequestedAction
  | FindDashboardConciliationsStateFailureAction
  | FindDashboardConciliationsStateSuccessAction
  | ChangeDashboardCountrySelectedAction
  | SetSingleCountryDashboardAction
  | FindDashboardConciliationsStateCardsRequestedAction
  | GetDashboardConciliationsStateYesterdaySuccessAction
  | GetDashboardConciliationsStateYesterdayFailureAction
  | GetDashboardConciliationsStateCurrentMonthSuccessAction
  | GetDashboardConciliationsStateCurrentMonthFailureAction
  | GetDashboardConciliationsStateLastMonthSuccessAction
  | GetDashboardConciliationsStateLastMonthFailureAction
  | ChangeAutoUpdateAction
  | FindMastersStatus2RequestedAction
  | FindMastersStatus2SuccessAction
  | FindMastersStatus2FailureAction
  | FindSovosImportStatusesRequestedAction
  | FindSovosImportStatusesSuccessAction
  | FindSovosImportStatusesFailureAction
  | FindDashboardTotalsCardsRequestedAction
  | GetDashboardTotalsYesterdaySuccessAction
  | GetDashboardTotalsYesterdayFailureAction
  | GetDashboardTotalsCurrentMonthSuccessAction
  | GetDashboardTotalsCurrentMonthFailureAction
  | GetDashboardTotalsLastMonthSuccessAction
  | GetDashboardTotalsLastMonthFailureAction
  | FindDashboardClosureEvolutionRequestedAction
  | GetDashboardClosureEvolutionSuccessAction
  | GetDashboardClosureEvolutionFailureAction
  | ChangeDashboardClosureEvolutionFilterStartAccountingDateAction
  | ChangeDashboardClosureEvolutionFilterEndAccountingDateAction;
